import React, { useState, useEffect, useCallback } from 'react';
import {
  Bug,
  Search,
  Filter,
  RefreshCw,
  Eye,
  Calendar,
  User,
  Paperclip,
  CheckCircle2,
  AlertCircle,
  Clock,
  Layers,
} from 'lucide-react';
import { PageHeader } from '@/components/common/PageHeader';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/common/EmptyState';
import { LoadingState } from '@/components/common/LoadingState';
import { BugReportRecord, BugStatus } from '@/types/database';
import { bugService } from '@/services/bugService';
import { BugDetailDrawer } from '@/components/bugs/BugDetailDrawer';
import { formatDate } from '@/lib/utils';

export const BugReportsPage: React.FC = () => {
  const [bugs, setBugs] = useState<BugReportRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [statusFilter, setStatusFilter] = useState<BugStatus | 'all'>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const pageSize = 25;

  const [selectedBug, setSelectedBug] = useState<BugReportRecord | null>(null);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);

  const fetchBugs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await bugService.getBugReports({
        status: statusFilter,
        search: searchTerm,
        page,
        pageSize,
      });
      setBugs(res.bugs);
      setTotalCount(res.totalCount);
    } catch (err) {
      console.error('Error fetching bugs:', err);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, searchTerm, page]);

  useEffect(() => {
    fetchBugs();
  }, [fetchBugs]);

  const handleRowClick = (bug: BugReportRecord) => {
    setSelectedBug(bug);
    setDrawerOpen(true);
  };

  const getStatusBadge = (status: BugStatus) => {
    switch (status) {
      case 'new':
        return <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-blue-100 text-blue-800 border border-blue-200">New</span>;
      case 'reviewing':
        return <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-indigo-100 text-indigo-800 border border-indigo-200">Reviewing</span>;
      case 'fixing':
        return <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-amber-100 text-amber-800 border border-amber-200">Fixing</span>;
      case 'ready_to_verify':
        return <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-teal-100 text-teal-800 border border-teal-200">Ready to Verify</span>;
      case 'closed':
        return <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-slate-100 text-slate-700 border border-slate-200">Closed</span>;
    }
  };

  const statusTabs: Array<{ id: BugStatus | 'all'; label: string }> = [
    { id: 'all', label: 'All Reports' },
    { id: 'new', label: 'New' },
    { id: 'reviewing', label: 'Reviewing' },
    { id: 'fixing', label: 'Fixing' },
    { id: 'ready_to_verify', label: 'Ready to Verify' },
    { id: 'closed', label: 'Closed' },
  ];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <PageHeader
        title="Bug Reports & Issue Triage"
        subtitle="Review, investigate, and manage in-app user problem reports and attached screenshots"
        icon={Bug}
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchBugs()}
            disabled={loading}
            className="text-xs font-semibold gap-1.5"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        }
      />

      {/* Filter and Search Controls */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 bg-white p-3 rounded-xl border border-slate-200 shadow-xs">
        {/* Status Tabs */}
        <div className="flex items-center gap-1 overflow-x-auto pb-1 sm:pb-0 no-scrollbar">
          {statusTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                setStatusFilter(tab.id);
                setPage(1);
              }}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors ${
                statusFilter === tab.id
                  ? 'bg-slate-900 text-white shadow-xs'
                  : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Search Bar */}
        <div className="relative w-full sm:w-72">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Search bug number, route, reporter..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(1);
            }}
            className="w-full pl-9 pr-3 py-1.5 text-xs rounded-lg border border-slate-200 bg-slate-50 focus:bg-white focus:outline-none focus:ring-1 focus:ring-emerald-600 text-slate-900 placeholder:text-slate-400 transition-all"
          />
        </div>
      </div>

      {/* Main Content Area */}
      {loading ? (
        <LoadingState message="Loading submitted bug reports..." />
      ) : bugs.length === 0 ? (
        <EmptyState
          title="No Bug Reports Found"
          description={
            searchTerm
              ? `No bug reports match your search "${searchTerm}".`
              : statusFilter !== 'all'
              ? `No reports currently in the "${statusFilter}" status.`
              : 'Great job! No bug reports have been submitted yet.'
          }
          icon={CheckCircle2}
        />
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 shadow-xs overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50/80 text-slate-600 uppercase font-bold text-[10px] tracking-wider">
                  <th className="py-3 px-4">Bug ID</th>
                  <th className="py-3 px-4">Status</th>
                  <th className="py-3 px-4">Description</th>
                  <th className="py-3 px-4">Reporter</th>
                  <th className="py-3 px-4">Page / Route</th>
                  <th className="py-3 px-4 text-center">Screenshots</th>
                  <th className="py-3 px-4 text-right">Reported At</th>
                  <th className="py-3 px-4 text-center">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-slate-800">
                {bugs.map((b) => (
                  <tr
                    key={b.id}
                    onClick={() => handleRowClick(b)}
                    className="hover:bg-slate-50/80 cursor-pointer transition-colors group"
                  >
                    <td className="py-3 px-4 font-mono font-bold text-slate-900 whitespace-nowrap">
                      {b.bug_number}
                    </td>
                    <td className="py-3 px-4 whitespace-nowrap">
                      {getStatusBadge(b.status)}
                    </td>
                    <td className="py-3 px-4 max-w-xs">
                      <p className="truncate font-medium text-slate-800">
                        {b.description}
                      </p>
                    </td>
                    <td className="py-3 px-4 whitespace-nowrap text-slate-600">
                      {b.reporter_name || b.reporter_email || 'Anonymous'}
                    </td>
                    <td className="py-3 px-4 whitespace-nowrap">
                      <code className="text-[11px] bg-slate-100 px-1.5 py-0.5 rounded text-slate-700 max-w-[140px] truncate inline-block">
                        {b.app_route}
                      </code>
                    </td>
                    <td className="py-3 px-4 text-center whitespace-nowrap">
                      {(b.attachments?.length || 0) > 0 ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-700">
                          <Paperclip className="w-3 h-3 text-slate-500" />
                          {b.attachments?.length}
                        </span>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-right whitespace-nowrap text-slate-500 text-[11px]">
                      {formatDate(b.created_at)}
                    </td>
                    <td className="py-3 px-4 text-center whitespace-nowrap">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs font-semibold text-slate-600 group-hover:text-emerald-700 group-hover:bg-emerald-50"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRowClick(b);
                        }}
                      >
                        <Eye className="w-3.5 h-3.5 mr-1" /> View
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination Footer */}
          <div className="p-3 border-t border-slate-200 bg-slate-50 flex items-center justify-between text-xs text-slate-500">
            <span>
              Showing {bugs.length} of {totalCount} reports
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="h-7 text-xs font-semibold"
              >
                Previous
              </Button>
              <span className="font-semibold text-slate-700">Page {page}</span>
              <Button
                variant="outline"
                size="sm"
                disabled={bugs.length < pageSize || page * pageSize >= totalCount}
                onClick={() => setPage((p) => p + 1)}
                className="h-7 text-xs font-semibold"
              >
                Next
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Bug Diagnostic Drawer */}
      <BugDetailDrawer
        bug={selectedBug}
        isOpen={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedBug(null);
        }}
        onUpdated={() => {
          fetchBugs();
        }}
      />
    </div>
  );
};
